import React from "react";
import Link from "next/link";
import { Ruler, Box, Layers, Wand2 } from "lucide-react";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import type { MarketingDiecutListItem } from "@/src/actions/marketing-diecuts";
import { useTranslation } from "react-i18next";

interface DiecutCardProps {
  dieCut: MarketingDiecutListItem;
  viewMode: "grid" | "list";
}

function formatDimensions(d: MarketingDiecutListItem): string {
  const parts = [d.length, d.width, d.height].filter(Boolean);
  if (parts.length === 0) return "—";
  return parts.join(" × ") + ` ${d.dimensionUnit}`;
}

export const DiecutCard: React.FC<DiecutCardProps> = ({ dieCut, viewMode }) => {
  const { t } = useTranslation("common");

  return (
    <GlassCard
      className={`overflow-hidden group ${viewMode === "list" ? "flex flex-col md:flex-row" : "flex flex-col"}`}
    >
      {/* Image */}
      <Link
        href={`/diecuts/${dieCut.slug}`}
        className={`relative overflow-hidden block ${viewMode === "list" ? "md:w-64 shrink-0" : "h-64"}`}
      >
        {dieCut.thumbnailUrl ? (
          <ImageWithFallback
            src={dieCut.thumbnailUrl}
            alt={dieCut.name ?? t("marketingPages.diecutCard.templateAlt")}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-walnut-100 dark:bg-walnut-900/50 flex items-center justify-center">
            <Box className="w-12 h-12 text-walnut-400 dark:text-walnut-600" />
          </div>
        )}
        {dieCut.isFeatured && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-walnut-600 dark:bg-walnut-500 text-white text-xs font-semibold">
            {t("marketingPages.diecutCard.featured")}
          </div>
        )}
        {dieCut.complexity && (
          <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full backdrop-blur-xl bg-black/40 text-white text-xs font-medium capitalize">
            {dieCut.complexity}
          </div>
        )}
      </Link>

      {/* Content */}
      <div className={`p-6 flex flex-col ${viewMode === "list" ? "flex-1" : "flex-1"}`}>
        <h3 className="text-xl font-bold text-walnut-900 dark:text-white mb-2">
          {dieCut.name}
        </h3>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-walnut-700 dark:text-walnut-300">
            <Ruler className="w-4 h-4" />
            <span>{formatDimensions(dieCut)}</span>
          </div>
          {dieCut.materialName && (
            <div className="flex items-center gap-2 text-sm text-walnut-700 dark:text-walnut-300">
              <Box className="w-4 h-4" />
              <span>{dieCut.materialName}</span>
            </div>
          )}
          {dieCut.categoryTitle && (
            <div className="flex items-center gap-2 text-sm text-walnut-700 dark:text-walnut-300">
              <Layers className="w-4 h-4" />
              <span>{dieCut.categoryTitle}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {dieCut.tags && dieCut.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {dieCut.tags.slice(0, 2).map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded-full bg-walnut-100 dark:bg-walnut-800 text-walnut-700 dark:text-walnut-300 text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div className="mt-auto flex gap-2 pt-2">
          <Link href={`/diecuts/${dieCut.slug}`} className="flex-1">
            <Button variant="glass" size="sm" className="w-full rounded-[20px]">
              {t("marketingPages.diecutCard.details")}
            </Button>
          </Link>
          <Link
            href={`/studio?diecutSlug=${dieCut.slug}&role=customer`}
            className="flex-1"
          >
            <Button
              variant="gradient"
              size="sm"
              className="w-full rounded-[20px]"
            >
              <Wand2 className="w-3.5 h-3.5" />
              {t("marketingPages.diecutCard.design")}
            </Button>
          </Link>
        </div>
      </div>
    </GlassCard>
  );
};
